// frontend/src/components/Dashboard/PaymentMethods.jsx - CLEANED
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCreditCard,
  faMobileAlt,
  faTrash,
  faPlus,
  faTimes,
  faCheckCircle,
  faSpinner,
  faExclamationTriangle,
  faWallet,
  faPhone,
  faCalendarAlt,
  faUser,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import { usePayment } from "../../context/PaymentContext";

const PaymentMethods = ({ onRefresh }) => {
  const {
    paymentMethods,
    loading,
    fetchPaymentMethods,
    addPayment,
    removePayment,
    setDefault,
  } = usePayment();

  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    type: "mobile_money",
    phoneNumber: "",
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    nameOnCard: "",
    isDefault: false,
  });

  const paymentTypes = [
    { value: "mobile_money", label: "Mobile Money", icon: faMobileAlt },
    { value: "orange_money", label: "Orange Money", icon: faMobileAlt },
    { value: "afrimoney", label: "Afrimoney", icon: faMobileAlt },
    { value: "wave", label: "WAVE", icon: faMobileAlt },
    { value: "card", label: "Credit/Debit Card", icon: faCreditCard },
  ];

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    const mobileTypes = ["mobile_money", "orange_money", "afrimoney", "wave"];
    if (mobileTypes.includes(formData.type)) {
      if (!formData.phoneNumber) {
        setError("Phone number is required for mobile money");
        setSubmitting(false);
        return;
      }
      const cleanPhone = formData.phoneNumber.replace(/\s/g, "");
      if (!/^(\+232|0)[7-8]\d{7}$/.test(cleanPhone)) {
        setError(
          "Please enter a valid Sierra Leone phone number (e.g., +23276123456 or 076123456)",
        );
        setSubmitting(false);
        return;
      }
    } else if (formData.type === "card") {
      if (
        !formData.cardNumber ||
        !formData.expiryDate ||
        !formData.cvv ||
        !formData.nameOnCard
      ) {
        setError("All card fields are required");
        setSubmitting(false);
        return;
      }
      const cleanCard = formData.cardNumber.replace(/\s/g, "");
      if (!/^\d{15,16}$/.test(cleanCard)) {
        setError("Please enter a valid card number (15-16 digits)");
        setSubmitting(false);
        return;
      }
    }

    try {
      const dataToSend = { type: formData.type, isDefault: formData.isDefault };
      if (formData.type === "card") {
        dataToSend.cardNumber = formData.cardNumber.replace(/\s/g, "");
        dataToSend.expiryDate = formData.expiryDate;
        dataToSend.cvv = formData.cvv;
        dataToSend.nameOnCard = formData.nameOnCard;
      } else {
        dataToSend.phoneNumber = formData.phoneNumber;
      }

      await addPayment(dataToSend);
      setSuccess("Payment method added successfully!");
      setFormData({
        type: "mobile_money",
        phoneNumber: "",
        cardNumber: "",
        expiryDate: "",
        cvv: "",
        nameOnCard: "",
        isDefault: false,
      });
      if (onRefresh) onRefresh();
      setTimeout(() => setShowModal(false), 1500);
    } catch (error) {
      console.error("Error adding payment method:", error);
      setError(error.response?.data?.message || "Failed to add payment method");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm("Are you sure you want to remove this payment method?"))
      return;
    try {
      await removePayment(id);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error removing payment method:", error);
      setError("Failed to remove payment method");
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await setDefault(id);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error setting default payment method:", error);
      setError("Failed to set default payment method");
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return "";
    if (phone.startsWith("+232")) return phone.replace("+232", "0");
    return phone;
  };

  const getProviderLabel = (type) => {
    const labels = {
      mobile_money: "Mobile Money",
      orange_money: "Orange Money",
      afrimoney: "Afrimoney",
      wave: "WAVE",
      card: "Credit/Debit Card",
    };
    return labels[type] || type.replace("_", " ").toUpperCase();
  };

  if (loading) {
    return (
      <div className="payment-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <span>Loading payment methods...</span>
      </div>
    );
  }

  return (
    <div className="payment-methods-container">
      <div className="payment-header">
        <h3>
          <FontAwesomeIcon icon={faWallet} /> Payment Methods
        </h3>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          <FontAwesomeIcon icon={faPlus} /> Add Payment Method
        </button>
      </div>

      {error && (
        <div className="payment-alert error">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{error}</span>
          <button onClick={() => setError("")}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}
      {success && (
        <div className="payment-alert success">
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>{success}</span>
          <button onClick={() => setSuccess("")}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      <div className="payment-methods-grid">
        {paymentMethods.length === 0 ? (
          <div className="no-payment-methods">
            <FontAwesomeIcon icon={faWallet} size="2x" />
            <p>No payment methods added yet.</p>
            <p className="sub-text">
              Add a payment method to make checkout faster.
            </p>
          </div>
        ) : (
          paymentMethods.map((method) => (
            <div
              key={method._id}
              className={`payment-method-card ${method.isDefault ? "default" : ""}`}
            >
              <div className="payment-method-icon">
                <FontAwesomeIcon
                  icon={method.type === "card" ? faCreditCard : faMobileAlt}
                />
              </div>
              <div className="payment-method-details">
                <div className="payment-method-type">
                  {getProviderLabel(method.type)}
                  {method.isDefault && (
                    <span className="default-badge">Default</span>
                  )}
                </div>
                <div className="payment-method-info">
                  {method.type === "card"
                    ? `**** **** **** ${method.last4}`
                    : formatPhone(method.phoneNumber)}
                  {method.type === "card" && method.nameOnCard && (
                    <span className="card-name">{method.nameOnCard}</span>
                  )}
                </div>
                <div className="payment-method-actions">
                  {!method.isDefault && (
                    <button
                      className="set-default-btn"
                      onClick={() => handleSetDefault(method._id)}
                    >
                      Set as Default
                    </button>
                  )}
                  <button
                    className="remove-btn"
                    onClick={() => handleRemove(method._id)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={faPlus} /> Add Payment Method
              </h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="payment-form">
              <div className="form-group">
                <label>Payment Type *</label>
                <div className="payment-type-selector">
                  {paymentTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      className={`type-btn ${formData.type === type.value ? "active" : ""}`}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, type: type.value }))
                      }
                    >
                      <FontAwesomeIcon icon={type.icon} />
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {formData.type !== "card" && (
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faPhone} /> Phone Number *
                    <small className="helper-text">
                      Sierra Leone format: +23276123456 or 076123456
                    </small>
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="+232 76 123456"
                    required
                  />
                </div>
              )}

              {formData.type === "card" && (
                <>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faCreditCard} /> Card Number *
                    </label>
                    <input
                      type="text"
                      name="cardNumber"
                      value={formData.cardNumber}
                      onChange={handleChange}
                      placeholder="1234 5678 9012 3456"
                      maxLength="19"
                      required
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faCalendarAlt} /> Expiry Date *
                      </label>
                      <input
                        type="text"
                        name="expiryDate"
                        value={formData.expiryDate}
                        onChange={handleChange}
                        placeholder="MM/YY"
                        maxLength="5"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faLock} /> CVV *
                      </label>
                      <input
                        type="password"
                        name="cvv"
                        value={formData.cvv}
                        onChange={handleChange}
                        placeholder="123"
                        maxLength="4"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faUser} /> Name on Card *
                    </label>
                    <input
                      type="text"
                      name="nameOnCard"
                      value={formData.nameOnCard}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                </>
              )}

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isDefault"
                    checked={formData.isDefault}
                    onChange={handleChange}
                  />
                  Set as default payment method
                </label>
              </div>

              {error && (
                <div className="form-error">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{error}</span>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> Adding...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlus} /> Add Payment Method
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethods;
